import React, { useEffect, useRef, useState } from 'react';
import { UrlParam } from 'types/Misc';
import { useParams } from 'react-router-dom';
import { isPreviewMode } from 'utils/preview';
import { useTranslation } from 'react-i18next';
import { isDesktop } from 'react-device-detect';
import { ctxRebateId } from 'utils/brijContext';
import { useGlobal } from 'context/global/GlobalContext';
import { getCustomData } from 'utils/customData';
import getMetaIntegration from 'utils/getMetaIntegration';
import getCookieConsent from 'utils/getCookieConsent';
import {
  getTiktokIntegration,
  getGoogleIntegration,
} from 'utils/adIdentifiers';
import { SmsOptInType } from 'types/SmsOptInTypes';
import { PossibleGtmEventNames, logRebateGtmEvent } from 'utils/logGtmEvent';
import { isLightColor, shadeChanger } from 'utils/shadeChanger';
import { showToast } from 'components/atomic/Toast/Toast';
import useRedirectLink from 'hooks/useRedirectLink';
import validator from 'validator';
import envVars from 'env-vars';
import { normalizePhoneNumber } from 'utils/normalizePhoneNumber';

type UseRebateOptInParams = {
  data: SmsOptInType | null;
  brandName: string;
  brandId: string;
  showRebateStatusPage: boolean;
};

export const useRebateOptIn = ({
  data,
  brandName,
  brandId,
  showRebateStatusPage,
}: UseRebateOptInParams) => {
  const [currentScreen, setCurrentScreen] = useState<'sms' | 'email'>('sms');
  const [emailConsentChecked, toggleEmailConsentChecked] =
    useState<boolean>(false);
  const [smsConsentChecked, toggleSmsConsentChecked] = useState<boolean>(false);
  const [numberField, toggleNumberField] = useState<boolean>(isDesktop);
  const [consentError, setConsentError] = useState<boolean>(false);
  const [otpRequired, setOtpRequired] = useState<boolean>(false);
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [textSent, toggleTextSent] = useState<boolean>(false);
  const [rebateEmail, setRebateEmail] = useState<string>('');
  const [textNumber, setTextNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>('');
  const [showRebateStatus, setShowRebateStatus] = useState<boolean>(() =>
    window.location.search.includes('rebateId=')
  );
  const [existingPhone, setExistingPhone] = useState<string | null>(null);
  const [checkingEmail, setCheckingEmail] = useState<boolean>(false);
  const [useEmailForOtp, setUseEmailForOtp] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  const { productDetails } = useGlobal();
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const redirectToExternalLink = useRedirectLink();
  const { id } = useParams<UrlParam>();
  const { t } = useTranslation('translation', { keyPrefix: 'smsOptIn' });

  useEffect(() => {
    if (data?.signupMethod === 'SMS_EMAIL') setCurrentScreen('email');
    else setCurrentScreen('sms');
  }, [data?.signupMethod]);

  useEffect(() => {
    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentCheckedByDefault
    )
      toggleEmailConsentChecked(true);
  }, [
    data?.optInComponent?.emailConsentBox,
    data?.optInComponent?.emailConsentCheckedByDefault,
  ]);

  useEffect(() => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.checkedAsDefault
    )
      toggleSmsConsentChecked(true);
  }, [data?.optInComponent]);

  useEffect(() => {
    if (!data?.enableTextToOptinOnMobile) toggleNumberField(true);
  }, [data?.enableTextToOptinOnMobile]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((seconds) => (seconds <= 1 ? 0 : seconds - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const defaultLegalSmsConsent = t('defaultSmsDisclaimerText')
    .replace('{{brand_name}}', brandName)
    .replaceAll('https://brij.it/terms', `/p/${brandId}`);

  const defaultLegalEmailConsent = t('defaultEmailLegalText')
    .replace('{{brand_name}}', brandName)
    .replace('https://brij.it/terms', `/p/${brandId}`);

  const isSmsConsentBlocking =
    !!data?.optInComponent?.consentBox &&
    !!data?.optInComponent?.consentRequired &&
    !smsConsentChecked;

  const isEmailConsentBlocking =
    !!data?.optInComponent?.emailConsentBox &&
    !!data?.optInComponent?.emailConsentRequired &&
    !emailConsentChecked;

  const getApiUrl = () =>
    envVars.REACT_APP_STAND_ALONE_MODE === 'false'
      ? envVars.REACT_APP_API_URL
      : '';

  // Phase 2: the rebate id lives only in the Redis session record. When it is
  // absent the opt-in flows block the request client-side (rebateIdError toast).
  const getRebateId = () => ctxRebateId(id) || '';

  const getCustomDataPayload = () => {
    const customDataObject = getCustomData();
    const hasCustomData = Object.keys(customDataObject).length > 0;
    return hasCustomData
      ? Object.entries(customDataObject).map(([key, value]) => ({
          key,
          value,
        }))
      : [];
  };

  const buildSmsConsentPayload = () => {
    const isEmailOnlyChannel = data?.communicationChannel === 'EMAIL';
    return {
      consentType: data?.optInComponent?.consentBox
        ? 'explicit'
        : isEmailOnlyChannel
        ? 'none'
        : 'implicit',
      consentFor: 'sms',
      defaultLanguage: defaultLegalSmsConsent,
      language: data?.customLegalConsent
        ? data?.customLegalText
        : t('defaultSmsDisclaimerText'),
      allowed: data?.optInComponent?.consentBox
        ? smsConsentChecked || null
        : isEmailOnlyChannel
        ? null
        : true,
    };
  };

  const buildEmailConsentPayload = () => ({
    consentType: data?.optInComponent?.emailConsentBox
      ? 'explicit'
      : 'implicit',
    consentFor: 'email',
    defaultLanguage: defaultLegalEmailConsent,
    language: data?.optInComponent?.customizeEmailConsentText
      ? data?.optInComponent?.emailConsentBoxText
      : defaultLegalEmailConsent,
    allowed: data?.optInComponent?.emailConsentBox
      ? emailConsentChecked || null
      : true,
  });

  // Rebate initiation. Fired only from the phone/email opt-in POST
  // (/app_api/rebate/:rebateId/text), which is the request that actually
  // creates the opt-in: it reaches shared-api init-rebate-user-setup, which
  // records REBATE_INITIATION and is what global-events-watcher maps to
  // rebateInitiationEvent.
  //
  // The Text-Us path is deliberately NOT an initiation. /app_api/rebate/
  // :rebateId/redirect only hands the shopper a deep link and records
  // REBATE_OPT_IN_WITH_TEXT_MESSAGE, which convertEventName does not map to any
  // rebate event; the shopper has not opted in until they actually send the
  // text, and that arrives over the Twilio webhook with no browser attached.
  // Firing here would emit initiations the server never counts.
  //
  // Gated on the same brand flag as the registration dataLayer events.
  const logRebateInitiation = (rebateId: string) => {
    if (!productDetails?.brand?.enableDataLayerEvents) return;
    logRebateGtmEvent(PossibleGtmEventNames.REBATE_INITIATION, {
      productDetails,
      rebateId,
      rebateStatus: 'REBATE_INITIATION',
      rebateSignUpMethod: 'Phone Number Entry',
      email: rebateEmail || null,
      // On the returning-customer flow the phone field is never mounted (the
      // shopper is identified by email and verified against the phone on
      // file), so textNumber is empty and the key is omitted rather than
      // guessed from the masked existingPhone.
      phoneNumber: textNumber || null,
      emailConsent: rebateEmail ? buildEmailConsentPayload().allowed : null,
      smsConsent: buildSmsConsentPayload().allowed,
    });
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length >= 4) {
      const lastFour = cleaned.slice(-4);
      return `(***) ***-${lastFour}`;
    }
    return phoneNumber;
  };

  const isOtpRequired = (): boolean =>
    data?.signupMethod === 'SMS_EMAIL' &&
    data?.communicationChannel !== 'SMS' &&
    rebateEmail !== '' &&
    validator.isEmail(rebateEmail);

  const validateEmail = (value: string) => {
    if (!value) {
      showToast({ message: t('emailInputError'), type: 'error' });
      return false;
    } else {
      if (validator.isEmail(value) && !value.toLowerCase().endsWith('.con')) {
        setConsentError(false);
        return true;
      } else {
        showToast({ message: t('invalidEmailError'), type: 'error' });
        return false;
      }
    }
  };

  const validatePhoneAndConsent = (): boolean => {
    if (!useEmailForOtp && textNumber.replace(/[\s()_-]/g, '').length !== 12) {
      showToast({ message: t('invalidPhoneNumberError'), type: 'error' });
      return false;
    }
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({ message: t('consentRequiredError'), type: 'error' });
      return false;
    } else {
      setConsentError(false);
    }
    return true;
  };

  const showOtpEntry = otpRequired && otpSent;
  const showTextSentSuccess = numberField && textSent;
  const showExistingProfileMessage =
    !!existingPhone &&
    ((data?.communicationChannel === 'EMAIL' &&
      !!data?.optInComponent?.consentBox) ||
      data?.communicationChannel === 'SMS_EMAIL');
  // The phone-verification header ("Verify Phone Number" + subtext) shows on the
  // phone step for every flow whose verification code is delivered by SMS to a
  // phone as part of an email-capable signup (EMAIL and SMS_EMAIL communication),
  // for both new and returning customers. Pure-SMS communication keeps its own
  // layout and intentionally does not show this header.
  const showVerifyPhoneHeader =
    !textSent &&
    !otpRequired &&
    (data?.communicationChannel === 'EMAIL' ||
      data?.communicationChannel === 'SMS_EMAIL');
  // The title shows whenever the header shows — including the returning-customer
  // "a profile already exists" case, where the title sits above that message.
  const showVerifyPhoneTitle = showVerifyPhoneHeader;
  const showSmsConsentCheck =
    !showOtpEntry && !!data?.optInComponent?.consentBox && !textSent;
  const showTextUsLink = data?.signupMethod === 'SMS' && !isDesktop;
  const showEmailSkipButton = data?.communicationChannel === 'SMS';
  const showEmailImplicitLegal =
    currentScreen === 'email' && !data?.optInComponent?.emailConsentBox;
  const showOtpLegal =
    currentScreen === 'sms' &&
    data?.communicationChannel === 'EMAIL' &&
    data?.signupMethod === 'SMS_EMAIL' &&
    !otpSent &&
    !data?.optInComponent?.consentBox;
  const showSmsImplicitLegal =
    currentScreen === 'sms' &&
    !data?.optInComponent?.consentBox &&
    !(
      data?.communicationChannel === 'EMAIL' &&
      data?.signupMethod === 'SMS_EMAIL'
    );

  const isLightBg = isLightColor(
    data?.optInComponent?.backgroundColor || '#FFFFFF',
    130
  );
  const bgTextColor = isLightBg ? '!text-black' : '!text-white/90';
  const legalParagraphColor = isLightBg
    ? shadeChanger('#000000', 50)
    : shadeChanger('#FFFFFF', 30);
  const consentMargin = isDesktop ? '!mb-1' : '!mb-2';

  const smsConsentText = data?.customLegalConsent
    ? data?.customLegalText
    : defaultLegalSmsConsent;

  const emailConsentText =
    data?.optInComponent?.emailConsentBoxText &&
    data?.optInComponent?.customizeEmailConsentText
      ? data?.optInComponent?.emailConsentBoxText
      : defaultLegalEmailConsent;

  const otpLegalHtml = t('defaultOtpText').replace(
    '{{privacy_page_url}}',
    isPreviewMode() ? `/c/${brandId}` : `/p/${brandId}`
  );

  const otpPhoneDisplay = existingPhone || formatPhoneNumber(textNumber);
  const existingPhoneFormatted = formatPhoneNumber(existingPhone || '');

  // The OTP is always delivered by SMS to the phone on file: shared-api
  // /otp/request resolves the email to the account's phone and texts the code
  // (there is no email-delivery path). So this copy references the phone number
  // for both the phone-entry flow and the returning-customer (useEmailForOtp)
  // flow, where otpPhoneDisplay is the masked on-file phone.
  const otpPasscodeSentText = t('otpPasscodeSentMessage').replace(
    '{{phoneNumber}}',
    otpPhoneDisplay
  );

  const emailPlaceholder =
    rebateEmail === ''
      ? t('rebateEmailInputPlaceholder')
      : t('rebateEmailInputFilledPlaceholder');
  const textUsButtonLabel = data?.enableTextToOptinOnMobile
    ? data?.buttonCTALinkToMessagingAppText
    : t('sendMessageButton');

  const getPhoneSubmitButtonLabel = (): string => {
    if (data?.optInComponent?.customButtonCTA)
      return data?.optInComponent?.customButtonCTAPhoneNumberSubmissionText;
    if (data?.communicationChannel === null) return t('receiveMessageButton');
    if (
      !otpSent &&
      data?.signupMethod !== 'SMS' &&
      data?.communicationChannel !== 'SMS'
    )
      return t('sendCodeButton');
    return t('receiveMessageButton');
  };
  const phoneSubmitButtonLabel = getPhoneSubmitButtonLabel();

  const existingPhoneButtonLabel = data?.optInComponent?.customButtonCTA
    ? data?.optInComponent?.customButtonCTAPhoneNumberSubmissionText
    : t('sendCodeButton');

  // The verification code is texted to the on-file phone in every case (shared-api
  // resolves email -> phone), so the identity header always references the phone
  // number rather than the email the returning customer entered.
  const verifyIdentityTitleKey = 'verifyPhoneNumberTitle';

  // The verification code is texted to the on-file phone in every case (see
  // otpPasscodeSentText), so the "you will get a code to ..." copy references the
  // masked phone number for both the returning-customer (useEmailForOtp) and
  // existing-phone flows.
  const verifyPhoneDescription = showExistingProfileMessage
    ? t('existingProfileMessage') + existingPhoneFormatted
    : existingPhone
    ? t('existingPhoneMessage') + existingPhoneFormatted
    : t('verifyPhoneNumberDescription');

  const getInputAreaHeight = (): string => {
    if (!numberField) return 'h-[40px]';
    if (textSent) return isDesktop ? 'pt-1 h-0' : 'h-0';
    if (showOtpEntry) return isDesktop ? 'pt-1 h-[180px]' : 'h-[180px]';
    if (existingPhone) return isDesktop ? 'pt-1 h-20' : 'h-20';
    if (data?.signupMethod === 'SMS' && !isDesktop) return 'h-[172px]';
    return isDesktop ? 'pt-1 h-[132px]' : 'h-[132px]';
  };
  const inputAreaHeight = getInputAreaHeight();
  const requestOtp = async (): Promise<boolean> => {
    const API_URL = getApiUrl();
    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail }
        : { phoneNumber: normalizePhoneNumber(textNumber) };

      const response = await fetch(`${API_URL}/shared-api/otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json().catch(() => null);

      if (response.ok && responseData?.success !== false) {
        // The code is always delivered by SMS to the phone on file (shared-api
        // resolves email -> phone), so the confirmation always references the phone.
        showToast({
          message: t('otpSentSuccess'),
          type: 'success',
        });
        setOtpSent(true);
        const cooldownSeconds =
          typeof responseData?.cooldownSeconds === 'number'
            ? responseData.cooldownSeconds
            : 30;
        setResendCooldown(cooldownSeconds);
        return true;
      } else {
        const message =
          responseData?.error || responseData?.message || t('otpSendError');
        showToast({ message, type: 'error' });
        if (typeof responseData?.retryAfterSeconds === 'number') {
          setResendCooldown(responseData.retryAfterSeconds);
        }
        return false;
      }
    } catch (error) {
      showToast({ message: t('otpSendError'), type: 'error' });
      console.error('Error requesting OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (): Promise<boolean> => {
    if (otpCode.length !== 6) {
      showToast({ message: t('otpInvalidLength'), type: 'error' });
      return false;
    }

    const API_URL = getApiUrl();
    setOtpLoading(true);
    try {
      const requestBody = useEmailForOtp
        ? { email: rebateEmail, code: otpCode }
        : {
            phoneNumber: normalizePhoneNumber(textNumber),
            code: otpCode,
            email: rebateEmail,
          };

      const response = await fetch(`${API_URL}/shared-api/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.success === false) {
          const errorMessage =
            responseData.message || responseData.error || t('otpVerifyError');
          showToast({ message: errorMessage, type: 'error' });
          return false;
        }
        return true;
      } else {
        showToast({ message: t('otpVerifyError'), type: 'error' });
        return false;
      }
    } catch (error) {
      showToast({ message: t('otpVerifyError'), type: 'error' });
      console.error('Error verifying OTP:', error);
      return false;
    } finally {
      setOtpLoading(false);
    }
  };

  const openSMSApp = async () => {
    if (
      data?.optInComponent?.consentBox &&
      data?.optInComponent?.consentRequired &&
      !smsConsentChecked
    ) {
      setConsentError(true);
      showToast({ message: t('consentRequiredError'), type: 'error' });
      return;
    } else setConsentError(false);

    const API_URL = getApiUrl();
    setLoading(true);
    const rebateId = getRebateId();

    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/redirect`;
      const customData = getCustomDataPayload();

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            consent: buildSmsConsentPayload(),
            ...(customData && { customData }),
            metaIntegration: getMetaIntegration(),
            tiktokIntegration: getTiktokIntegration(),
            googleIntegration: getGoogleIntegration(),
            cookieConsent: getCookieConsent(
              productDetails?.brand?.cookieConsentSettings
            ),
          }),
        });

        if (!response.ok) {
          console.error('Error:', await response.json());
          showToast({ message: t('errorSendingRequest'), type: 'error' });
        } else {
          const data = await response.json();
          redirectToExternalLink(data.redirectUrl, '_self');
        }
      } catch (error) {
        showToast({ message: t('errorSendingRequest'), type: 'error' });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  // Post-submission copy is driven by the experience's configured communication
  // channel so an email-only rebate isn't told to check for a text (and vice
  // versa). Each caller passes its own key set; SMS and the unknown (null) case
  // fall back to the provided base key.
  const channelKey = (keys: {
    email: string;
    sms: string;
    both: string;
    fallback: string;
  }) => {
    switch (data?.communicationChannel) {
      case 'EMAIL':
        return keys.email;
      case 'SMS':
        return keys.sms;
      case 'SMS_EMAIL':
        return keys.both;
      default:
        return keys.fallback;
    }
  };

  // Toast shown after a successful rebate submission.
  const submissionSuccessMessageKey = channelKey({
    email: 'messageSentSuccessfullyEmail',
    sms: 'messageSentSuccessfullySms',
    both: 'messageSentSuccessfullyBoth',
    fallback: 'messageSentSuccessfully',
  });

  // Persistent "we sent you something, don't see it?" banner. The base
  // messageSentText is already the text/SMS-specific copy, so SMS reuses it.
  const messageSentTextKey = channelKey({
    email: 'messageSentTextEmail',
    sms: 'messageSentText',
    both: 'messageSentTextBoth',
    fallback: 'messageSentText',
  });

  const sendOptInRequest = async () => {
    const API_URL = getApiUrl();
    setLoading(true);
    const rebateId = getRebateId();

    if (rebateId) {
      const url = `${API_URL}/app_api/rebate/${rebateId}/text`;
      const customData = getCustomDataPayload();

      const body = {
        phoneNumber: normalizePhoneNumber(textNumber),
        consent: buildSmsConsentPayload(),
        email: rebateEmail || '',
        ...(rebateEmail && {
          emailConsent: buildEmailConsentPayload(),
        }),
        ...(customData && { customData }),
        metaIntegration: getMetaIntegration(),
        tiktokIntegration: getTiktokIntegration(),
        googleIntegration: getGoogleIntegration(),
        cookieConsent: getCookieConsent(
          productDetails?.brand?.cookieConsentSettings
        ),
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          // Prefer the API's own error text when it provides one — the
          // backend distinguishes max-redemptions from other failure modes
          // via the message itself, so surfacing it verbatim (with the
          // legacy "Error sending text:" prefix stripped and the first
          // letter capitalized) keeps user-facing copy in sync with the
          // backend without a hardcoded English branch. Falls back to the
          // localized generic error if no message comes through.
          let errorMessage = t('errorSendingRequest');

          try {
            const errorData = await response.json();
            console.error('Error:', errorData);

            const rawErrorMessage =
              errorData?.error || errorData?.message || '';

            const cleanedMessage = rawErrorMessage
              ? rawErrorMessage.replace(/^Error sending text:\s*/i, '').trim()
              : '';

            if (cleanedMessage) {
              errorMessage =
                cleanedMessage.charAt(0).toUpperCase() +
                cleanedMessage.slice(1);
            }
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
          }

          showToast({ message: errorMessage, type: 'error' });
        } else {
          showToast({
            message: t(submissionSuccessMessageKey),
            type: 'success',
          });
          logRebateInitiation(rebateId);
          if (showRebateStatusPage) {
            window.history.pushState(
              {},
              '',
              `${window.location.pathname}?rebateId=${rebateId}`
            );
            setShowRebateStatus(true);
          }
          toggleTextSent(true);
        }
      } catch (error) {
        showToast({ message: t('errorSendingRequest'), type: 'error' });
        console.error('Error:', error);
      }
    } else showToast({ message: t('rebateIdError'), type: 'error' });
    setLoading(false);
  };

  const handlePhoneSubmit = async () => {
    if (!validatePhoneAndConsent()) return;

    if (isOtpRequired() && !otpSent) {
      const success = await requestOtp();
      if (success) setOtpRequired(true);
      return;
    }
    await sendOptInRequest();
  };

  const handleOtpSubmit = async () => {
    const verified = await verifyOtp();
    if (verified) {
      await sendOptInRequest();
    }
  };

  const canResendOtp = resendCooldown === 0 && !otpLoading;

  const handleResendOtp = async () => {
    if (!canResendOtp) return;
    setOtpCode('');
    await requestOtp();
  };

  const handleRebateEmailSubmission = async (emailSkipped: boolean = false) => {
    if (emailSkipped) {
      // The user explicitly chose to skip the email step, so any value they
      // typed must be discarded — it should not be saved to customer details,
      // sent in events, used for OTP, or shown in the rebate pop-up (AA-435).
      // The submission body gates both `email` and `emailConsent` on a
      // truthy `rebateEmail`, so clearing it here keeps the skipped email out
      // of every downstream request.
      setRebateEmail('');
      toggleEmailConsentChecked(false);
      setUseEmailForOtp(false);
      setConsentError(false);
      setCurrentScreen('sms');
      return;
    }

    if (
      data?.optInComponent?.emailConsentBox &&
      data?.optInComponent?.emailConsentRequired &&
      !emailConsentChecked
    ) {
      setConsentError(true);
      return;
    }

    if (!validateEmail(rebateEmail)) return;

    const API_URL = getApiUrl();
    setCheckingEmail(true);
    try {
      const response = await fetch(
        `${API_URL}/shared-api/users/check-rebates-email?email=${encodeURIComponent(
          rebateEmail
        )}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.exists && data.truncatedPhone) {
          setExistingPhone(data.truncatedPhone);
          setUseEmailForOtp(true);
        } else {
          setExistingPhone(null);
          setUseEmailForOtp(false);
        }
      }
    } catch (error) {
      console.error('Error checking email:', error);
      setExistingPhone(null);
      setUseEmailForOtp(false);
    } finally {
      setCheckingEmail(false);
      setCurrentScreen('sms');
    }
  };

  const handleOtpDigitChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return;

    if (digits.length > 1) {
      const pasted = digits.slice(0, 6);
      setOtpCode(pasted);
      const focusIdx = Math.min(pasted.length, 5);
      setTimeout(() => otpInputRefs.current[focusIdx]?.focus(), 0);
      return;
    }

    const digit = digits;
    let newCode: string;
    if (index < otpCode.length) {
      newCode =
        otpCode.substring(0, index) + digit + otpCode.substring(index + 1);
    } else {
      newCode = otpCode + digit;
    }
    setOtpCode(newCode.slice(0, 6));

    if (index < 5) {
      setTimeout(() => otpInputRefs.current[index + 1]?.focus(), 0);
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (/^[0-9]$/.test(e.key) && otpCode[index]) {
      e.preventDefault();
      const newCode =
        otpCode.substring(0, index) + e.key + otpCode.substring(index + 1);
      setOtpCode(newCode.slice(0, 6));
      if (index < 5) {
        setTimeout(() => otpInputRefs.current[index + 1]?.focus(), 0);
      }
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      if (otpCode[index]) {
        const newCode =
          otpCode.substring(0, index) + otpCode.substring(index + 1);
        setOtpCode(newCode);
      } else if (index > 0) {
        otpInputRefs.current[index - 1]?.focus();
        const newCode =
          otpCode.substring(0, index - 1) + otpCode.substring(index);
        setOtpCode(newCode);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (pasted) {
      setOtpCode(pasted);
      const focusIdx = Math.min(pasted.length, 5);
      setTimeout(() => otpInputRefs.current[focusIdx]?.focus(), 0);
    }
  };

  const handleOtpFocus = (index: number) => {
    const nextEmpty = otpCode.length;
    if (index > nextEmpty && nextEmpty < 6) {
      otpInputRefs.current[nextEmpty]?.focus();
      return;
    }
    otpInputRefs.current[index]?.select();
  };

  // Handed to RebateStatus so the rebate SUBMISSION event can carry the same
  // customer identity the INITIATION event did.
  //
  // Gated on textSent, which is set ONLY in the sendOptInRequest success branch
  // and therefore means "this shopper completed the opt-in in this mounted
  // session". The status page is also a cold entry point - Brij texts and
  // emails a /c/{slug}?rebateId={id} link and showRebateStatus initialises
  // straight from that query param - and on a cold load the consent builders
  // would still report consent derived purely from campaign CONFIG: buildSms
  // ConsentPayload().allowed returns literal true whenever there is no consent
  // box, and the checkedAsDefault mount effect re-checks the box on every
  // mount. A shopper who explicitly DECLINED sms marketing at opt-in would then
  // have customerSMSSingleConsent: true stamped on their submission event, which
  // brands forward to Klaviyo/Attentive as a consent record.
  //
  // So a cold load reports nothing rather than something invented: identity and
  // both consents are null, and buildRebateEventData omits those keys. Consent
  // for those submissions has to come from the server, which holds the real
  // stored permission.
  const optedInThisSession = textSent;
  const rebateCustomerSnapshot = {
    email: optedInThisSession ? rebateEmail || null : null,
    phoneNumber: optedInThisSession ? textNumber || null : null,
    emailConsent:
      optedInThisSession && rebateEmail
        ? buildEmailConsentPayload().allowed
        : null,
    smsConsent: optedInThisSession ? buildSmsConsentPayload().allowed : null,
  };

  return {
    t,

    rebateCustomerSnapshot,
    currentScreen,
    rebateEmail,
    setRebateEmail,
    textNumber,
    setTextNumber,
    otpCode,
    existingPhone,
    numberField,
    toggleNumberField,
    textSent,
    smsConsentChecked,
    toggleSmsConsentChecked,
    emailConsentChecked,
    toggleEmailConsentChecked,
    consentError,
    showRebateStatus,
    loading,
    otpLoading,
    checkingEmail,
    otpInputRefs,
    resendCooldown,
    canResendOtp,

    messageSentTextKey,

    showOtpEntry,
    showTextSentSuccess,
    showVerifyPhoneHeader,
    showVerifyPhoneTitle,
    showSmsConsentCheck,
    showTextUsLink,
    showEmailSkipButton,
    showEmailImplicitLegal,
    showOtpLegal,
    showSmsImplicitLegal,
    isSmsConsentBlocking,
    isEmailConsentBlocking,

    textUsButtonLabel,
    phoneSubmitButtonLabel,
    existingPhoneButtonLabel,
    verifyPhoneDescription,
    verifyIdentityTitleKey,
    inputAreaHeight,
    bgTextColor,
    legalParagraphColor,
    consentMargin,
    smsConsentText,
    emailConsentText,
    otpLegalHtml,
    otpPhoneDisplay,
    otpPasscodeSentText,
    existingPhoneFormatted,
    emailPlaceholder,

    openSMSApp,
    handlePhoneSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleOtpDigitChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleOtpFocus,
    handleRebateEmailSubmission,
  };
};
